import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useRoutes } from 'react-router-dom';

import { getAktivitetDomaner } from 'store/aktivitetDomanerSlice';
import { getBebyggelseobjektDomaner } from 'store/bebyggelseobjektDomanerSlice';
import { getConfiguration } from 'store/configurationSlice';
import { setDrawerOpen, setDrawerSize } from 'store/drawerSlice';
import { getKommun, getLan } from 'store/geoReferensdataSlice';
import { RootState } from 'store/rootReducer';
import { initializeUser } from 'store/userSlice';
import { getUtforare } from 'store/utforareSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useNavigatorOnLine } from 'hooks/useNavigatorOnLine';

import { RaaDrawer, RaaSpinner } from 'raa-react';

import ErrorBoundary from 'components/errorBoundary/ErrorBoundary';
import ErrorDialog from 'components/errorDialog/ErrorDialog';
import MapComponent from 'components/map/Map';
import Topbar from 'components/topbar/Topbar';

import { AppRoutes } from './Router';

import { baseHref } from 'utilities/getBaseHref';

function App() {
  const dispatch = useAppDispatch();
  const drawer = useAppSelector((state: RootState) => state.drawer);
  const configurationData = useAppSelector((state: RootState) => state.configuration.data);
  const aktivitetDomaner = useAppSelector((state: RootState) => state.aktivitetDomaner.data);

  const navigate = useNavigate();
  const { hasNoAuthorization, selected_organisation, rolesInSelectedOrganisation, isAnonymous } = useAppSelector(
    (state: RootState) => state.user
  );

  const error = useAppSelector((state: RootState) => state.error);

  const location = useLocation();
  const [initialized, setInitialized] = useState<boolean>(false);
  const { drawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    const initialize = () =>
      Promise.all([
        dispatch(getConfiguration()),
        dispatch(getBebyggelseobjektDomaner()),
        dispatch(getAktivitetDomaner())
      ]);

    void initialize().then(() => setInitialized(true));

    dispatch(getUtforare());
    dispatch(getKommun());
    dispatch(getLan());
  }, [dispatch]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' && configurationData?.matomoContainerId) {
      const _paq: Window['_paq'] = (window._paq = window._paq || []);
      _paq.push(['requireCookieConsent']);

      const _mtm: Window['_mtm'] = (window._mtm = window._mtm || []);
      _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });

      const d = document,
        g = d.createElement('script'),
        s = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = `https://webbanalys.sgit.se/raa/matomo/js/container_${configurationData?.matomoContainerId}.js`;
      s.parentNode?.insertBefore(g, s);
    }
  }, [configurationData?.matomoContainerId]);

  useEffect(() => {
    if (aktivitetDomaner) {
      void dispatch(initializeUser());
    }
  }, [aktivitetDomaner, dispatch]);

  useEffect(() => {
    if (!isAnonymous && hasNoAuthorization) {
      window.location.href = `https://${window.location.host}/401.html`;
    }
  }, [hasNoAuthorization, isAnonymous]);

  useEffect(() => {
    if (error.globalRequestError) {
      throw new Error(error.globalRequestError.message);
    }
  }, [error.globalRequestError]);

  useEffect(() => {
    dispatch(setDrawerOpen(location.pathname !== '/' ? 'open' : 'closed'));

    const size =
      (location.pathname.includes('bebyggelseobjekt') || location.pathname.includes('utkast')) &&
      !location.pathname.includes('sok-bebyggelseobjekt')
        ? 'medium'
        : location.pathname.includes('/aktiviteter')
          ? 'fullscreen'
          : 'small';

    dispatch(setDrawerSize(size));
  }, [location, dispatch]);

  useEffect(() => {
    if (error.notFound) {
      navigate(`/404`, { replace: true });
    }
  }, [navigate, error.notFound]);

  useNavigatorOnLine();

  const bebyggelseRoutes = AppRoutes(rolesInSelectedOrganisation);

  const allRoutes = [
    {
      path: '/',
      element: <Outlet />,
      children: bebyggelseRoutes
    }
  ];

  const routes = useRoutes(allRoutes);

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <ErrorDialog />
      <Topbar />
      <main className="tw-relative tw-h-full">
        {configurationData?.mapUrl && <MapComponent />}
        {location.pathname !== baseHref && (
          <RaaDrawer
            drawerSize={drawer.drawerSize}
            drawerOpen={drawer.drawerVisibility}
            drawerHandleClicked={() =>
              dispatch(setDrawerOpen(drawer.drawerVisibility === 'open' ? 'minimized' : 'open'))
            }
            drawerInvisible={drawer.invisible}
            handleIsVisible={!location.pathname.includes('/aktiviteter')}
            mobileTemplate={drawerMobileTemplate ? () => drawerMobileTemplate : () => <></>}
          >
            {!initialized && (
              <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
                <RaaSpinner />
              </div>
            )}
            <ErrorBoundary>{(selected_organisation || isAnonymous) && initialized && routes}</ErrorBoundary>
          </RaaDrawer>
        )}
      </main>
    </div>
  );
}

export default App;
